'use client';

import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { cn } from '@/src/shared/lib/utils';

interface OrderCardProps {
  order: {
    id: string;
    title: string;
    description?: string;
    location?: string;
    deadline?: string;
    startedAt?: string;
  };
  participant: {
    id: string;
    name: string;
    displayName?: string;
    avatarUrl?: string;
  };
  onLocationClick?: () => void;
  onViewOrder?: () => void;
  className?: string;
}

/**
 * Format deadline for display
 */
function formatDeadline(deadline?: string, startedAt?: string): string {
  if (!deadline && !startedAt) {
    return '';
  }

  try {
    if (deadline) {
      const date = new Date(deadline);
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      return `Deadline: ${formatted}`;
    }

    if (startedAt) {
      const date = new Date(startedAt);
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      return `Started: ${formatted}`;
    }

    return '';
  } catch (err) {
    console.error('Failed to format deadline:', err);
    return '';
  }
}

/**
 * OrderCard - Order details card for chat
 * Displays order information in the chat interface following the design spec
 */
export const OrderCard = ({
  order,
  participant,
  onLocationClick,
  onViewOrder,
  className,
}: OrderCardProps) => {
  const participantName = participant?.displayName || participant?.name || 'User';
  const deadlineText = formatDeadline(order?.deadline, order?.startedAt);

  return (
    <div
      className={cn(
        'self-stretch p-5 bg-background-primary rounded-[20px]',
        'border border-accent-secondary',
        'flex flex-col justify-start items-center gap-5',
        className
      )}
    >
      {/* Header with Avatar and Details */}
      <div className="self-stretch inline-flex justify-center items-start gap-5">
        {/* Avatar - 80x80 per design */}
        <Avatar
          src={participant?.avatarUrl}
          alt={participantName}
          size="xl"
          fallback={participantName.charAt(0)}
          className="w-20 h-20 flex-shrink-0"
        />

        {/* Details */}
        <div className="flex-1 flex justify-between items-start">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-2.5">
            {/* Top row: Name and Deadline */}
            <div className="self-stretch inline-flex justify-between items-center">
              <span className="text-text-secondary text-xl font-medium font-['Roboto'] leading-6">
                {participantName}
              </span>
              {deadlineText && (
                <div className="flex justify-end items-end gap-[5px]">
                  <span className="text-text-secondary text-base font-normal font-['Roboto'] leading-5">
                    {deadlineText}
                  </span>
                </div>
              )}
            </div>

            {/* Order Title */}
            <span className="justify-start text-text-primary text-xl font-medium font-['Roboto'] leading-6">
              {order?.title || 'Order'}
            </span>

            {/* Location and "See location" button */}
            {order?.location && (
              <div className="self-stretch inline-flex justify-between items-center">
                <span className="justify-start text-text-primary text-xl font-medium font-['Roboto'] leading-6">
                  {order.location}
                </span>
                <button
                  onClick={onLocationClick}
                  className="h-6 py-3 rounded-[750px] flex justify-center items-center gap-2 hover:opacity-70 transition-opacity"
                >
                  <span className="justify-start text-accent-primary text-xl font-medium font-['Roboto'] leading-6">
                    See location
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="self-stretch h-0 border-t border-accent-secondary" />

      {/* Description */}
      {order?.description && (
        <div className="self-stretch flex flex-col justify-start items-start gap-5">
          <div className="self-stretch flex flex-col justify-start items-start gap-[5px]">
            <p className="self-stretch justify-start text-text-primary text-base font-normal font-['Roboto'] leading-5">
              {order.description}
            </p>
          </div>
        </div>
      )}

      {/* View Order button */}
      {onViewOrder && (
        <button
          onClick={onViewOrder}
          className="self-stretch flex justify-center items-center gap-2 py-2 rounded-[10px] hover:bg-background-secondary transition-colors"
        >
          <span className="text-accent-primary text-base font-medium font-['Roboto'] leading-5">
            View Order
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-accent-primary"
          >
            <path d="M5 12h14" />
            <path d="M12 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};
